import { Color, Flex, Icon, Link, Space, Spacing, Switch, Text, Tooltip, useModal } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../../../../../../../../../enums/modalId'
import { useFormContext } from '../../../../../../../../../../../../hooks'
import { LegalSettingTypes } from '../../../../../../enums/legalSettingTypes'
import { InvoiceSettingsFormSchema } from '../../../../../../types/invoiceSettingsFormSchema'

interface LegalSettingSwitchProps {
  legalSettingType: LegalSettingTypes
}

export const LegalSettingSwitch = ({ legalSettingType }: LegalSettingSwitchProps): ReactElement => {
  const { t } = useTranslation()
  const { setValue: setInvoiceSettingsFormValue, watch } = useFormContext<InvoiceSettingsFormSchema>()
  const { open: openLegalGuaranteeOfConformityModal } = useModal(ModalId.LegalGuaranteeOfConformityModal)

  const isChecked = watch(`legalFormSettings.${legalSettingType}`)
  const isLegalGuaranteeOfConformityChecked = watch('legalFormSettings.isLegalGuaranteeOfConformity')

  const isLegalGuaranteeOfConformity = legalSettingType === LegalSettingTypes.IsLegalGuaranteeOfConformity

  const { annotation, description, learnMore, title } = useMemo(() => {
    switch (legalSettingType) {
      case LegalSettingTypes.IsLegalGuaranteeOfConformity:
        return {
          title: t('settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.title'),
          description: t(
            'settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.description',
          ),
          learnMore: t(
            'settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.learn_more',
          ),
          annotation: t(
            'settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.annotation',
          ),
        }
      case LegalSettingTypes.IsMemberOfAccreditedAssociation:
        return {
          title: t('settings.organization.external_invoice.legal_mentions.member_of_accredited_association.title'),
          description: t(
            'settings.organization.external_invoice.legal_mentions.member_of_accredited_association.description',
          ),
        }
      case LegalSettingTypes.IsVatOnDebitsEnabled:
        return {
          title: t('settings.organization.external_invoice.legal_mentions.vat_on_debits.title'),
          description: t('settings.organization.external_invoice.legal_mentions.vat_on_debits.description'),
        }
    }
  }, [legalSettingType, t])

  const handleSwitchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setInvoiceSettingsFormValue(`legalFormSettings.${legalSettingType}`, event.target.checked, { shouldDirty: true })
    },
    [legalSettingType, setInvoiceSettingsFormValue],
  )

  const handleLearnMoreClick = useCallback(() => {
    openLegalGuaranteeOfConformityModal()
  }, [openLegalGuaranteeOfConformityModal])

  return (
    <Flex gap={Spacing.S8} alignItems="flex-start" justifyContent="space-between">
      <Flex.Item>
        <Text as="span" colorVariant="primary" weight="bold">
          {title}
        </Text>{' '}
        {isLegalGuaranteeOfConformity ? (
          <>
            <Space size="s4" />
            <Flex flexDirection="column" alignItems="flex-start" gap={Spacing.S4}>
              <Text colorVariant="secondary">{description}</Text>
              <Link underlined={false} onClick={handleLearnMoreClick}>
                <Text as="span" color={Color.Lavender500} weight="bold">
                  {learnMore}
                </Text>
              </Link>
              {isLegalGuaranteeOfConformityChecked && (
                <Text colorVariant="secondary" variant="bodySM">
                  {annotation}
                </Text>
              )}
            </Flex>
          </>
        ) : (
          <Tooltip as="span" label={description} placement="top">
            <Icon icon="info" size="sm" />
          </Tooltip>
        )}
      </Flex.Item>

      <Switch checked={isChecked} onChange={handleSwitchChange} />
    </Flex>
  )
}
